import React, { useMemo, useState } from 'react';
import { addDays, addWeeks, startOfWeek } from 'date-fns';
import {
  BellRing,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronRight as ChevronCollapsed,
  RotateCcw,
  X,
} from 'lucide-react';

import PageCard from '../../PageCard/PageCard';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { cn } from '../../../lib/utils';
import { SheetStatus, WeekSheet } from '../../../model/organization.model';
import {
  formatHours,
  members,
  projectById,
  weekEntries,
  weekSheets,
} from './orgMock';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const weekLabel = (start: Date, end: Date) =>
  start.getMonth() === end.getMonth()
    ? `${MONTHS_FULL[start.getMonth()]} ${start.getDate()}–${end.getDate()}`
    : `${MONTHS_SHORT[start.getMonth()]} ${start.getDate()} – ${MONTHS_SHORT[end.getMonth()]} ${end.getDate()}`;

const STATUS_META: Record<SheetStatus, { label: string; className: string }> = {
  submitted: { label: 'Pending', className: 'border-amber-500/40 text-amber-700 dark:text-amber-400' },
  approved: { label: 'Approved', className: 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400' },
  rejected: { label: 'Sent back', className: 'border-destructive/40 text-destructive' },
  open: { label: 'Not submitted', className: 'border-border text-muted-foreground' },
};

const StatusPill: React.FC<{ status: SheetStatus }> = ({ status }) => (
  <span
    className={cn(
      'inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-xs',
      STATUS_META[status].className,
    )}
  >
    {STATUS_META[status].label}
  </span>
);

const ApprovalsPage: React.FC = () => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [sheets, setSheets] = useState<WeekSheet[]>(weekSheets);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [reminded, setReminded] = useState<string[]>([]);

  const days = useMemo(() => {
    const monday = startOfWeek(addWeeks(new Date(), weekOffset), { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, index) => addDays(monday, index));
  }, [weekOffset]);

  const rows = useMemo(
    () =>
      sheets.map(sheet => {
        const entries = weekEntries.filter(item => item.memberId === sheet.memberId);
        const hours = entries.reduce((acc, item) => acc + item.hours.reduce((a, b) => a + b, 0), 0);
        const billable = entries
          .filter(item => item.billable)
          .reduce((acc, item) => acc + item.hours.reduce((a, b) => a + b, 0), 0);
        return {
          sheet,
          member: members.find(item => item.id === sheet.memberId)!,
          entries,
          hours,
          billable,
        };
      }),
    [sheets],
  );

  const pending = rows.filter(row => row.sheet.status === 'submitted');
  const totals = {
    pending: pending.length,
    approved: rows.filter(row => row.sheet.status === 'approved').length,
    rejected: rows.filter(row => row.sheet.status === 'rejected').length,
    missing: rows.filter(row => row.sheet.status === 'open').length,
    hours: rows.reduce((acc, row) => acc + row.hours, 0),
  };

  const setStatus = (memberId: string, status: SheetStatus, reviewNote?: string) =>
    setSheets(prev =>
      prev.map(sheet => (sheet.memberId === memberId ? { ...sheet, status, reviewNote } : sheet)),
    );

  const approveAll = () =>
    setSheets(prev =>
      prev.map(sheet => (sheet.status === 'submitted' ? { ...sheet, status: 'approved' } : sheet)),
    );

  const toggle = (memberId: string) =>
    setExpanded(prev =>
      prev.includes(memberId) ? prev.filter(id => id !== memberId) : [...prev, memberId],
    );

  const startReject = (memberId: string) => {
    setRejecting(memberId);
    setRejectNote('');
    setExpanded(prev => (prev.includes(memberId) ? prev : [...prev, memberId]));
  };

  const confirmReject = (memberId: string) => {
    setStatus(memberId, 'rejected', rejectNote.trim() || undefined);
    setRejecting(null);
    setRejectNote('');
  };

  const title = (
    <div className="flex items-center gap-2">
      <div className="flex items-center rounded-md border border-border">
        <button
          type="button"
          onClick={() => setWeekOffset(offset => offset - 1)}
          className="rounded-l-md px-2 py-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          title="Previous week"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => setWeekOffset(offset => offset + 1)}
          className="rounded-r-md border-l border-border px-2 py-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          title="Next week"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div>
        <div className="text-base font-medium leading-tight">
          Timesheets · {weekLabel(days[0], days[6])}
        </div>
        <div className="text-xs text-muted-foreground">Team · 6 people</div>
      </div>
    </div>
  );

  const extra = (
    <Button
      variant="primary"
      size="sm"
      className="dark:text-primary-foreground"
      disabled={totals.pending === 0}
      onClick={approveAll}
    >
      <Check />
      Approve all ({totals.pending})
    </Button>
  );

  return (
    <PageCard title={title} extra={extra}>
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Pending" value={totals.pending} tone={totals.pending > 0 ? 'warn' : 'plain'} />
        <Stat label="Approved" value={totals.approved} tone="good" />
        <Stat label="Sent back" value={totals.rejected} tone={totals.rejected > 0 ? 'bad' : 'plain'} />
        <Stat label="Not submitted" value={totals.missing} tone={totals.missing > 0 ? 'warn' : 'plain'} />
        <Stat label="Hours this week" value={formatHours(totals.hours)} tone="plain" />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-separate border-spacing-0">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-muted-foreground">
              <th className="border-b border-border px-3 py-2 text-left font-normal">Person</th>
              <th className="border-b border-border px-3 py-2 text-left font-normal">Team</th>
              <th className="w-24 border-b border-border px-3 py-2 text-right font-normal">Hours</th>
              <th className="w-28 border-b border-border px-3 py-2 text-right font-normal">Billable</th>
              <th className="w-40 border-b border-border px-3 py-2 text-left font-normal">Status</th>
              <th className="w-52 border-b border-border px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map(({ sheet, member, entries, hours, billable }) => {
              const isExpanded = expanded.includes(member.id);
              const isRejecting = rejecting === member.id;

              return (
                <React.Fragment key={member.id}>
                  <tr className={cn(sheet.status === 'submitted' && 'bg-amber-500/[0.04]')}>
                    <td className="border-b border-border/50 px-3 py-2">
                      <button
                        type="button"
                        onClick={() => toggle(member.id)}
                        disabled={entries.length === 0}
                        className="flex items-center gap-2 text-left text-sm disabled:opacity-60"
                      >
                        {entries.length > 0 &&
                          (isExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          ) : (
                            <ChevronCollapsed className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          ))}
                        {entries.length === 0 && <span className="w-3.5" />}
                        <span className="font-medium">{member.name}</span>
                      </button>
                    </td>
                    <td className="border-b border-border/50 px-3 py-2 text-sm text-muted-foreground">
                      {member.team}
                    </td>
                    <td className="border-b border-border/50 px-3 py-2 text-right text-sm tabular-nums">
                      {hours > 0 ? formatHours(hours) : <span className="text-muted-foreground/40">·</span>}
                    </td>
                    <td className="border-b border-border/50 px-3 py-2 text-right text-sm tabular-nums text-muted-foreground">
                      {hours > 0 ? formatHours(billable) : '·'}
                    </td>
                    <td className="border-b border-border/50 px-3 py-2">
                      <StatusPill status={sheet.status} />
                    </td>
                    <td className="border-b border-border/50 px-3 py-2">
                      <div className="flex items-center justify-end gap-1.5">
                        {sheet.status === 'submitted' && (
                          <>
                            <Button size="sm" onClick={() => setStatus(member.id, 'approved')}>
                              <Check />
                              Approve
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => startReject(member.id)}>
                              <X />
                              Send back
                            </Button>
                          </>
                        )}
                        {sheet.status === 'rejected' && (
                          <Button size="sm" variant="ghost" onClick={() => setStatus(member.id, 'submitted')}>
                            <RotateCcw />
                            Back to queue
                          </Button>
                        )}
                        {sheet.status === 'open' &&
                          (reminded.includes(member.id) ? (
                            <span className="text-xs text-muted-foreground">Reminder sent</span>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setReminded(prev => [...prev, member.id])}
                            >
                              <BellRing />
                              Remind
                            </Button>
                          ))}
                        {sheet.status === 'approved' && (
                          <span className="text-xs text-muted-foreground">{sheet.submittedAt}</span>
                        )}
                      </div>
                    </td>
                  </tr>

                  {sheet.status === 'rejected' && sheet.reviewNote && (
                    <tr>
                      <td colSpan={6} className="border-b border-border/50 px-3 pb-2">
                        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                          {sheet.reviewNote}
                        </div>
                      </td>
                    </tr>
                  )}

                  {isExpanded && (
                    <tr>
                      <td colSpan={6} className="border-b border-border/50 bg-muted/30 px-3 py-3">
                        <table className="w-full border-separate border-spacing-0">
                          <thead>
                            <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
                              <th className="py-1 text-left font-normal">Project · Work type</th>
                              {DAY_LABELS.map(label => (
                                <th key={label} className="w-14 py-1 text-center font-normal">
                                  {label}
                                </th>
                              ))}
                              <th className="w-16 py-1 text-right font-normal">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {entries.map((item, index) => {
                              const project = projectById(item.projectId);
                              const rowTotal = item.hours.reduce((a, b) => a + b, 0);
                              return (
                                <tr key={`${item.projectId}-${item.workType}-${index}`}>
                                  <td className="py-1 text-sm">
                                    <span className="flex items-center gap-2">
                                      <span
                                        className="h-2 w-2 shrink-0 rounded-sm"
                                        style={{ backgroundColor: project?.color }}
                                      />
                                      <span className="text-muted-foreground">{project?.client}</span>
                                      <span className="text-muted-foreground/50">·</span>
                                      <span>{project?.name}</span>
                                      <span className="text-muted-foreground/50">·</span>
                                      <span className="text-foreground/80">{item.workType}</span>
                                      {!item.billable && (
                                        <span className="rounded border border-border px-1 text-[10px] uppercase text-muted-foreground">
                                          non-billable
                                        </span>
                                      )}
                                    </span>
                                  </td>
                                  {item.hours.map((value, day) => (
                                    <td
                                      key={day}
                                      className={cn(
                                        'py-1 text-center text-sm tabular-nums',
                                        day >= 5 && 'text-muted-foreground',
                                        value >= 10 && 'font-medium text-amber-600 dark:text-amber-500',
                                      )}
                                    >
                                      {value ? formatHours(value) : <span className="text-muted-foreground/30">·</span>}
                                    </td>
                                  ))}
                                  <td className="py-1 text-right text-sm tabular-nums">{formatHours(rowTotal)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        {isRejecting && (
                          <div className="mt-3 rounded-md border border-border bg-card p-3">
                            <div className="mb-2 text-sm font-medium">What needs fixing?</div>
                            <Textarea
                              autoFocus
                              rows={2}
                              value={rejectNote}
                              onChange={event => setRejectNote(event.target.value)}
                              placeholder="This comment goes to the person along with the returned sheet"
                            />
                            <div className="mt-2 flex justify-end gap-2">
                              <Button size="sm" variant="ghost" onClick={() => setRejecting(null)}>
                                Cancel
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => confirmReject(member.id)}>
                                Send back for rework
                              </Button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Once approved, entries are locked — the client gets a rejection if it tries to change them.
      </p>
    </PageCard>
  );
};

const Stat: React.FC<{ label: string; value: React.ReactNode; tone: 'plain' | 'good' | 'warn' | 'bad' }> = ({
  label,
  value,
  tone,
}) => (
  <div className="rounded-md border border-border px-3 py-2">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div
      className={cn(
        'text-xl font-semibold tabular-nums',
        tone === 'good' && 'text-emerald-600 dark:text-emerald-500',
        tone === 'warn' && 'text-amber-600 dark:text-amber-500',
        tone === 'bad' && 'text-destructive',
      )}
    >
      {value}
    </div>
  </div>
);

export default ApprovalsPage;
